import { useState } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Mail, MessageCircle, MapPin, Phone, Clock, Send, Heart, Globe2, ShieldCheck, Instagram } from 'lucide-react'
import SiteLayout, { PageIntro } from './SiteLayout'
import PricingSection from './PricingSection'
import { PRIVACY, TERMS } from '../../content/legal'
import { POSTS, postBySlug } from '../../content/blog'
import { SITE } from '../../config/site'
import { t, getLanguage, formatDate } from '../../i18n'

const lang = () => (getLanguage() === 'ar' ? 'ar' : 'en')
const fill = text => text.split('{site}').join(t(SITE.name))

export function PricingPage() {
  return (
    <SiteLayout>
      <PageIntro eyebrow={t('Pricing')} title={t('Simple monthly plans. No commission.')}>
        {t('Start with a free trial, then pick the plan that fits. Change or cancel any time.')}
      </PageIntro>
      <PricingSection heading={false} compare />
      <section className="mx-auto max-w-3xl px-4 pb-20 sm:px-6">
        <div className="rounded-2xl bg-slate-50 p-6 text-center">
          <p className="font-semibold">{t('Need something custom for a group or a franchise?')}</p>
          <Link to="/contact" className="mt-2 inline-flex items-center gap-2 font-semibold text-blue-700">{t('Talk to us')} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
        </div>
      </section>
    </SiteLayout>
  )
}

export function AboutPage() {
  const values = [
    { icon: Heart, title: 'Built for local businesses', desc: 'Restaurants, shops and salons in Oman and the Gulf, with Arabic first-class and prices in OMR and SAR.' },
    { icon: Globe2, title: 'Online in minutes', desc: 'Your website, orders, delivery and bookings without hiring developers or paying commission.' },
    { icon: ShieldCheck, title: 'Your data stays yours', desc: 'Card payments go straight to your account, customer data is protected, and you can export it any time.' },
  ]
  return (
    <SiteLayout>
      <PageIntro eyebrow={t('About us')} title={t('Helping Gulf businesses sell online')}>
        {t('{v0} gives restaurants, stores and service businesses the tools big brands use - in a form a small team can run every day.', { v0: SITE.name })}
      </PageIntro>
      <section className="mx-auto max-w-3xl space-y-5 px-4 py-14 text-lg leading-8 text-slate-700 sm:px-6">
        <p>{t('We started with a simple observation: many great local businesses still take orders by phone and WhatsApp, write prices by hand, and lose orders when they are busy.')}</p>
        <p>{t('So we built one platform where a business gets its own website, receives orders and bookings, sets its delivery prices city by city, and gets paid - in Arabic and English, on any phone.')}</p>
        <p>{t('We are based in {v0} and we answer support ourselves.', { v0: t(SITE.city) })}</p>
      </section>
      <section className="mx-auto grid max-w-6xl gap-6 px-4 pb-20 sm:px-6 md:grid-cols-3">
        {values.map(v => (
          <div key={v.title} className="rounded-2xl border border-slate-200 p-6">
            <v.icon className="h-6 w-6 text-blue-700" />
            <h2 className="mt-4 font-bold">{t(v.title)}</h2>
            <p className="mt-1.5 text-slate-600">{t(v.desc)}</p>
          </div>
        ))}
      </section>
    </SiteLayout>
  )
}

export function ContactPage() {
  const [form, setForm] = useState({ name: '', business: '', message: '' })
  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }))
  const body = `${form.message}\n\n— ${form.name}${form.business ? ` (${form.business})` : ''}`
  const send = e => {
    e.preventDefault()
    if (SITE.whatsapp) window.open(`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(body)}`, '_blank', 'noopener')
    else if (SITE.email) window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(t('Question from the website'))}&body=${encodeURIComponent(body)}`
  }
  const channels = [
    SITE.whatsapp && { icon: MessageCircle, label: 'WhatsApp', value: `+${SITE.whatsapp}`, href: `https://wa.me/${SITE.whatsapp}` },
    SITE.email && { icon: Mail, label: 'Email', value: SITE.email, href: `mailto:${SITE.email}` },
    SITE.phone && { icon: Phone, label: 'Phone', value: SITE.phone, href: `tel:${SITE.phone.replace(/\s/g, '')}` },
    SITE.instagram && { icon: Instagram, label: 'Instagram', value: SITE.instagram.replace(/^https?:\/\/(www\.)?/, ''), href: SITE.instagram },
    { icon: MapPin, label: 'Office', value: t(SITE.city) },
    { icon: Clock, label: 'Support hours', value: t('Sunday to Thursday, 9:00 - 18:00') },
  ].filter(Boolean)
  const canSend = SITE.whatsapp || SITE.email
  const field = 'mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20'

  return (
    <SiteLayout>
      <PageIntro eyebrow={t('Contact')} title={t('We are here to help')}>
        {t('Questions about plans, setting up your website or moving from another system? Send us a message.')}
      </PageIntro>
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_1.2fr]">
        <ul className="space-y-3">
          {channels.map(c => (
            <li key={c.label} className="flex items-center gap-4 rounded-2xl border border-slate-200 p-4">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-blue-700"><c.icon className="h-5 w-5" /></span>
              <span className="min-w-0">
                <span className="block text-xs text-slate-500">{t(c.label)}</span>
                {c.href ? <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="block truncate font-semibold text-slate-900 hover:text-blue-700" dir="ltr">{c.value}</a>
                  : <span className="block font-semibold text-slate-900">{c.value}</span>}
              </span>
            </li>
          ))}
        </ul>
        {canSend ? (
          <form onSubmit={send} className="rounded-2xl border border-slate-200 p-6 sm:p-8">
            <h2 className="text-xl font-bold">{t('Send us a message')}</h2>
            <label className="mt-5 block text-sm font-medium text-slate-700">{t('Your name')}<input required maxLength={80} value={form.name} onChange={set('name')} className={field} /></label>
            <label className="mt-4 block text-sm font-medium text-slate-700">{t('Business name (optional)')}<input maxLength={80} value={form.business} onChange={set('business')} className={field} /></label>
            <label className="mt-4 block text-sm font-medium text-slate-700">{t('How can we help?')}<textarea required rows={5} maxLength={1500} value={form.message} onChange={set('message')} className={field} /></label>
            <button className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-700 py-3 font-semibold text-white hover:bg-blue-800"><Send className="h-4 w-4 rtl:rotate-180" />{SITE.whatsapp ? t('Send on WhatsApp') : t('Send by email')}</button>
          </form>
        ) : (
          <div className="rounded-2xl border border-slate-200 p-8">
            <h2 className="text-xl font-bold">{t('Already a customer?')}</h2>
            <p className="mt-2 text-slate-600">{t('Sign in to your dashboard - the setup guide and settings pages answer most questions.')}</p>
            <a href="/admin/login" className="mt-5 inline-flex rounded-xl bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800">{t('Log in')}</a>
          </div>
        )}
      </section>
    </SiteLayout>
  )
}

function LegalPage({ doc }) {
  const content = doc[lang()]
  return (
    <SiteLayout>
      <PageIntro title={content.title}>
        <span className="text-sm">{t('Last updated')}: {formatDate(SITE.legalUpdated)}</span>
      </PageIntro>
      <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        <p className="text-lg leading-8 text-slate-700">{fill(content.intro)}</p>
        {content.sections.map((s, i) => (
          <section key={s.h} className="mt-10">
            <h2 className="text-xl font-bold text-slate-900">{i + 1}. {s.h}</h2>
            {s.p.length > 1
              ? <ul className="mt-3 list-disc space-y-2 ps-5 leading-7 text-slate-700">{s.p.map(x => <li key={x}>{fill(x)}</li>)}</ul>
              : <p className="mt-3 leading-7 text-slate-700">{fill(s.p[0])}</p>}
          </section>
        ))}
        <section className="mt-12 rounded-2xl bg-slate-50 p-6">
          <h2 className="font-bold">{t('Contact')}</h2>
          <p className="mt-1 text-slate-700">{t('Questions about this page? Contact {v0}', { v0: SITE.company })}{SITE.email ? ': ' : '.'}{SITE.email && <a className="font-semibold text-blue-700" href={`mailto:${SITE.email}`}>{SITE.email}</a>}</p>
          {!SITE.email && <Link to="/contact" className="mt-2 inline-block font-semibold text-blue-700">{t('Contact us')}</Link>}
        </section>
      </article>
    </SiteLayout>
  )
}

export const PrivacyPage = () => <LegalPage doc={PRIVACY} />
export const TermsPage = () => <LegalPage doc={TERMS} />

function PostCard({ post, large = false }) {
  const c = post[lang()]
  return (
    <Link to={`/blog/${post.slug}`} className={`group flex flex-col rounded-2xl border border-slate-200 p-6 transition hover:border-blue-300 hover:shadow-lg ${large ? 'md:col-span-2 md:p-10' : ''}`}>
      <span className="text-xs font-semibold text-blue-700">{post.tag[lang()]}</span>
      <h2 className={`mt-2 font-extrabold tracking-tight text-slate-900 group-hover:text-blue-800 ${large ? 'text-2xl sm:text-3xl' : 'text-xl'}`}>{c.title}</h2>
      <p className="mt-2 flex-1 leading-7 text-slate-600">{c.excerpt}</p>
      <p className="mt-5 text-sm text-slate-500">{formatDate(post.date)} · {t('{v0} min read', { v0: post.minutes })}</p>
    </Link>
  )
}

export function BlogPage() {
  const [first, ...rest] = POSTS
  return (
    <SiteLayout>
      <PageIntro eyebrow={t('Blog')} title={t('Ideas to sell more, online and in store')}>
        {t('Practical guides for restaurants, shops and service businesses in Oman and the Gulf.')}
      </PageIntro>
      <section className="mx-auto grid max-w-6xl gap-6 px-4 py-14 sm:px-6 md:grid-cols-2">
        <PostCard post={first} large />
        {rest.map(p => <PostCard key={p.slug} post={p} />)}
      </section>
    </SiteLayout>
  )
}

export function BlogPostPage() {
  const { slug } = useParams()
  const post = postBySlug(slug)
  if (!post) return <Navigate to="/blog" replace />
  const c = post[lang()]
  const more = POSTS.filter(p => p.slug !== slug).slice(0, 2)
  return (
    <SiteLayout>
      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4 rtl:rotate-180" />{t('All articles')}</Link>
        <p className="mt-8 text-sm font-semibold text-blue-700">{post.tag[lang()]}</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-5xl">{c.title}</h1>
        <p className="mt-4 text-sm text-slate-500">{formatDate(post.date)} · {t('{v0} min read', { v0: post.minutes })}</p>
        <p className="mt-8 text-xl leading-8 text-slate-700">{c.excerpt}</p>
        <div className="mt-8 space-y-5 text-lg leading-8 text-slate-700">
          {c.body.map((block, i) => block.startsWith('## ')
            ? <h2 key={i} className="pt-4 text-2xl font-bold text-slate-900">{block.slice(3)}</h2>
            : <p key={i}>{block}</p>)}
        </div>
        <div className="mt-12 rounded-2xl bg-blue-800 p-8 text-white">
          <p className="text-xl font-bold">{t('Try it on your own business')}</p>
          <p className="mt-1 text-blue-100">{t('Create your website for free and follow the setup guide.')}</p>
          <Link to="/get-started" className="mt-5 inline-flex rounded-xl bg-white px-5 py-3 font-semibold text-blue-900 hover:bg-blue-50">{t('Start free')}</Link>
        </div>
      </article>
      <section className="border-t border-slate-100 bg-slate-50/70">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <h2 className="text-xl font-bold">{t('Keep reading')}</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">{more.map(p => <PostCard key={p.slug} post={p} />)}</div>
        </div>
      </section>
    </SiteLayout>
  )
}
